import {PartyType} from '@sollapay/enums';
import {
  CorpIdField,
  FieldDatePicker,
  FieldInput,
  FormSectionHeading,
  Icon,
  NationalIdField,
  PhoneNumberField
} from '@sollapay/ui/components';
import {Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {FullNameField} from '@/components';
import {ControlledAddressField} from '@/features/address';
import {BuyerKycDocumentUpload} from '@/features/buyers/components';
import {useRefreshBuyerDetail} from '@/features/buyers/hooks';
import {
  generateIsraeliAddress,
  generatePhoneNumber,
  withDebugFieldChange,
  withDebugSetValue
} from '@/features/debug-test-data';
import {
  companyIdRule,
  emailRule,
  fullNameRule,
  futureDateRule,
  nationalIdRule,
  phoneRule
} from '@/lib/validation';

import {useIdentityLock} from './useIdentityLock';

import type {EditBuyerFormValues} from './useEditBuyerForm';
import type {FieldLock} from './useIdentityLock';
import type {DebugSetValue} from '@/features/debug-test-data';
import type {FC} from 'react';
import type {UseFormReturn} from 'react-hook-form';

export interface EditCorporateBuyerFieldsProps {
  form: UseFormReturn<EditBuyerFormValues>;
  trustAccountId: string;
  buyerId: string;
  isIdentityLocked: boolean;
  identityLockedTooltip?: string;
  addressLock: FieldLock;
}

export const EditCorporateBuyerFields: FC<EditCorporateBuyerFieldsProps> = ({
  form,
  trustAccountId,
  buyerId,
  isIdentityLocked,
  identityLockedTooltip,
  addressLock
}) => {
  const {t} = useTranslation('trustAccounts');
  const {
    register,
    control,
    formState: {errors}
  } = form;
  const refreshBuyer = useRefreshBuyerDetail(trustAccountId, buyerId);
  const prefix = 'view.buyers.addBuyerModal.corporateDetails';
  const setDebugValue = form.setValue as DebugSetValue;
  const {identityLock, whenEditable} = useIdentityLock(isIdentityLocked, identityLockedTooltip);
  return (
    <div className="flex flex-col gap-6">
      <BuyerKycDocumentUpload
        partyType={PartyType.ORGANIZATION}
        trustAccountId={trustAccountId}
        metadata={{partyId: buyerId}}
        skipProcessing
        onDocumentChange={refreshBuyer}
      />

      <div className="grid grid-cols-2 gap-6">
        <FieldInput
          label={t(`${prefix}.companyName`)}
          placeholder={t(`${prefix}.companyNamePlaceholder`)}
          error={errors.fullName?.message}
          required
          startAdornment={<Icon name="building" className="size-4" />}
          {...identityLock}
          {...register(
            'fullName',
            whenEditable({required: t('common:fields.fullName.required_organization')})
          )}
        />
        <Controller
          name="incorporationNumber"
          control={control}
          rules={whenEditable(companyIdRule(t))}
          render={({field}) => (
            <CorpIdField
              label={t(`${prefix}.companyNumber`)}
              placeholder={t(`${prefix}.companyNumberPlaceholder`)}
              error={errors.incorporationNumber?.message}
              required
              {...identityLock}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <Controller
          name="incorporationDate"
          control={control}
          rules={whenEditable({
            required: t('common:fields.incorporationDate.required'),
            ...futureDateRule(t)
          })}
          render={({field}) => (
            <FieldDatePicker
              label={t(`${prefix}.incorporationDate`)}
              placeholder={t(`${prefix}.incorporationDatePlaceholder`)}
              error={errors.incorporationDate?.message}
              required
              disableFuture
              {...identityLock}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
        <ControlledAddressField
          control={control}
          name="address"
          label={t(`${prefix}.registeredAddress`)}
          error={errors.address?.message}
          validateRequired={!addressLock.isLocked}
          {...addressLock}
          cornerBadge={withDebugSetValue(
            undefined,
            setDebugValue,
            'address',
            generateIsraeliAddress
          )}
        />
      </div>

      <FormSectionHeading
        title={t(`${prefix}.signatoryTitle`)}
        description={t(`${prefix}.signatorySubtitle`)}
      />

      <div className="grid grid-cols-2 gap-6">
        <FullNameField
          label={t(`${prefix}.signatoryName`)}
          error={errors.signatory?.fullName?.message}
          required
          {...identityLock}
          {...register('signatory.fullName', whenEditable(fullNameRule(t)))}
        />
        <Controller
          name="signatory.nationalId"
          control={control}
          rules={whenEditable(nationalIdRule(t))}
          render={({field}) => (
            <NationalIdField
              label={t(`${prefix}.signatoryNationalId`)}
              placeholder={t(`${prefix}.signatoryNationalIdPlaceholder`)}
              error={errors.signatory?.nationalId?.message}
              required
              {...identityLock}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <FieldInput
          label={t(`${prefix}.signatoryEmail`)}
          placeholder={t(`${prefix}.signatoryEmailPlaceholder`)}
          error={errors.signatory?.email?.message}
          type="email"
          required
          startAdornment={<Icon name="mail" className="size-4" />}
          {...register('signatory.email', emailRule(t))}
        />
        <Controller
          name="signatory.phone"
          control={control}
          rules={phoneRule(t)}
          render={({field}) => (
            <PhoneNumberField
              label={t(`${prefix}.signatoryPhone`)}
              placeholder={t(`${prefix}.signatoryPhonePlaceholder`)}
              error={errors.signatory?.phone?.message}
              required
              cornerBadge={withDebugFieldChange(undefined, field.onChange, generatePhoneNumber)}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          )}
        />
      </div>
    </div>
  );
};
